import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, brl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import SignaturePad from "../components/SignaturePad";
import { toast } from "sonner";
import { CheckCircle2, XCircle, FileDown, ShieldCheck } from "lucide-react";

export default function PublicProposal() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [proposal, setProposal] = useState(null);
  const [brand, setBrand] = useState({});
  const [signerName, setSignerName] = useState("");
  const [signerEmail, setSignerEmail] = useState("");
  const [signature, setSignature] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [approving, setApproving] = useState(false);

  useEffect(() => {
    (async () => {
      const r = await api.get(`/public/proposals/${token}`);
      setProposal(r.data.proposal);
      setBrand(r.data.brand);
      setSignerName(r.data.proposal.client_name || "");
      setSignerEmail(r.data.proposal.client_email || "");
      api.post(`/public/proposals/${token}/view`).catch(() => {});
    })();
  }, [token]);

  const toggleOptional = async (line_id, selected) => {
    const r = await api.patch(`/public/proposals/${token}/items`, { line_id, selected });
    setProposal((p) => ({ ...p, items: r.data.items, totals: r.data.totals }));
  };

  const approve = async () => {
    if (approving) return;
    if (!signature) return toast.error("Assine para aprovar");
    setApproving(true);
    try {
      const r = await api.post(`/public/proposals/${token}/approve`, {
        signature_data_url: signature, signer_name: signerName, signer_email: signerEmail,
      });
      toast.success(r.data.already_approved ? "Proposta já aprovada. Abrindo contrato…" : "Proposta aprovada! Abrindo contrato…");
      if (r.data.contract_token) {
        navigate(`/c/${r.data.contract_token}`, { state: { payment_link: r.data.payment_link } });
      } else {
        // Fallback: reload proposal state so the "Ver contrato" button appears
        const p = await api.get(`/public/proposals/${token}`);
        setProposal(p.data.proposal);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Erro ao aprovar. Tente novamente.";
      toast.error(msg);
      // Refresh state so UI reflects real backend status
      try {
        const p = await api.get(`/public/proposals/${token}`);
        setProposal(p.data.proposal);
      } catch {}
    } finally {
      setApproving(false);
    }
  };

  const reject = async () => {
    await api.post(`/public/proposals/${token}/reject`, { reason: rejectReason });
    toast.info("Proposta recusada");
    const r = await api.get(`/public/proposals/${token}`);
    setProposal(r.data.proposal);
  };

  if (!proposal) return <div className="min-h-screen flex items-center justify-center overline">Carregando…</div>;
  const finalized = ["approved", "rejected", "expired"].includes(proposal.status);
  const primary = brand.brand_primary || "#C07C88";
  const gold = brand.brand_accent || "#D4AF37";

  return (
    <div className="min-h-screen bg-[#FAFAFA] py-10 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="doc-paper p-10 md:p-14">
          <div className="flex items-start justify-between mb-8 pb-6 border-b border-border">
            <div className="flex items-center gap-4">
              {brand.logo_url ? (
                <img src={brand.logo_url} alt="" className="h-12" />
              ) : (
                <div className="w-12 h-12 rounded-md flex items-center justify-center text-white font-heading text-xl" style={{ background: primary }}>{(brand.company_name || "L")[0]}</div>
              )}
              <div>
                <p className="font-heading text-xl">{brand.company_name || "Leadera Corp"}</p>
                <p className="text-xs text-muted-foreground">{brand.contact_email}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="overline">Proposta</p>
              <p className="font-mono text-sm">{proposal.number}</p>
              <p className="text-xs text-muted-foreground mt-1">Válido até {proposal.expires_at?.slice(0, 10)}</p>
            </div>
          </div>

          <div className="mb-8">
            <p className="overline">Preparado para</p>
            <p className="font-heading text-3xl mt-1">{proposal.client_name}</p>
            <p className="text-sm text-muted-foreground">{proposal.client_email} · {proposal.client_document}</p>
            <h2 className="font-heading text-2xl mt-6" style={{ color: primary }}>{proposal.title}</h2>
          </div>

          <div className="mb-8">
            <p className="overline mb-3">Escopo & investimento</p>
            <div className="border border-border rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#FAFAFA] text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="p-3"></th>
                    <th className="p-3">Item</th>
                    <th className="p-3 text-right">Qtd</th>
                    <th className="p-3 text-right">Unit.</th>
                    <th className="p-3 text-right">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {proposal.items.map((it) => (
                    <tr key={it.line_id} className={`border-t border-border ${!it.selected && "opacity-40"}`} data-testid={`public-item-${it.line_id}`}>
                      <td className="p-3">
                        {it.optional && !finalized ? (
                          <Checkbox checked={it.selected} onCheckedChange={(v) => toggleOptional(it.line_id, !!v)} data-testid={`toggle-opt-${it.line_id}`} />
                        ) : (
                          <span className={`text-xs ${it.optional ? "text-[#D4AF37]" : "text-muted-foreground"}`}>{it.optional ? "OPCIONAL" : "•"}</span>
                        )}
                      </td>
                      <td className="p-3">
                        <p className="font-medium">{it.name}</p>
                        {it.description && <p className="text-xs text-muted-foreground">{it.description}</p>}
                      </td>
                      <td className="p-3 text-right">{it.quantity} {it.unit}</td>
                      <td className="p-3 text-right">{brl(it.price)}</td>
                      <td className="p-3 text-right font-medium">{brl(it.quantity * it.price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div>
              {proposal.terms && (<><p className="overline mb-2">Termos</p><p className="text-sm text-muted-foreground whitespace-pre-wrap">{proposal.terms}</p></>)}
            </div>
            <Card className="p-5 border-border shadow-none">
              <p className="overline">Total</p>
              <div className="text-sm space-y-1 mt-3">
                <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span>{brl(proposal.totals.subtotal)}</span></div>
                <div className="flex justify-between text-muted-foreground"><span>Desconto</span><span>- {brl(proposal.totals.discount)}</span></div>
                <div className="flex justify-between text-muted-foreground"><span>Impostos</span><span>{brl(proposal.totals.tax)}</span></div>
                <div className="flex justify-between text-muted-foreground"><span>Envio</span><span>{brl(proposal.totals.shipping)}</span></div>
                <div className="flex justify-between pt-3 border-t border-border mt-3">
                  <span className="font-heading">TOTAL</span>
                  <span className="font-heading text-2xl" style={{ color: gold }} data-testid="public-total">{brl(proposal.totals.total)}</span>
                </div>
              </div>
            </Card>
          </div>

          {finalized ? (
            <div className="text-center py-8 border-t border-border">
              <p className="overline">Status</p>
              <p className="font-heading text-2xl mt-2" style={{ color: proposal.status === "approved" ? "#10B981" : "#EF4444" }}>
                {proposal.status === "approved" ? "APROVADO" : proposal.status === "rejected" ? "RECUSADO" : "VENCIDO"}
              </p>
              {proposal.status === "approved" && proposal.contract_token && (
                <Button className="mt-6" onClick={() => navigate(`/c/${proposal.contract_token}`)} style={{ background: gold, color: "#fff" }} data-testid="view-contract-btn">
                  Ver contrato
                </Button>
              )}
            </div>
          ) : (
            <div className="border-t border-border pt-8">
              <div className="grid md:grid-cols-2 gap-4">
                <Dialog>
                  <DialogTrigger asChild>
                    <Button className="w-full h-14 text-base font-medium" style={{ background: primary, color: "#fff" }} data-testid="approve-btn">
                      <CheckCircle2 size={18} className="mr-2" /> Aprovar Proposta
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle>Confirmação com assinatura</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                      <div><label className="text-xs uppercase tracking-wider">Nome completo</label><Input value={signerName} onChange={(e) => setSignerName(e.target.value)} data-testid="signer-name" /></div>
                      <div><label className="text-xs uppercase tracking-wider">E-mail</label><Input value={signerEmail} onChange={(e) => setSignerEmail(e.target.value)} data-testid="signer-email" /></div>
                      <SignaturePad onChange={setSignature} testid="approve-signature" />
                      <div className="text-xs text-muted-foreground flex items-start gap-2 pt-2">
                        <ShieldCheck size={14} className="mt-0.5 shrink-0" />
                        <span>Ao aprovar, você concorda com os termos. Serão registrados: IP, navegador, data/hora e assinatura para trilha de auditoria (MP 2.200-2/BR).</span>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button style={{ background: primary, color: "#fff" }} onClick={approve} disabled={approving} data-testid="confirm-approve-btn">
                        {approving ? "Processando…" : "Aprovar e gerar contrato"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="w-full h-14 text-base" data-testid="reject-btn"><XCircle size={18} className="mr-2" /> Recusar</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Motivo da recusa</DialogTitle></DialogHeader>
                    <Textarea rows={4} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} data-testid="reject-reason" />
                    <DialogFooter><Button variant="destructive" onClick={reject} data-testid="confirm-reject-btn">Confirmar recusa</Button></DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
              <div className="text-center mt-6">
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/public/proposals/${token}/pdf`} target="_blank" rel="noreferrer" className="text-xs text-muted-foreground hover:text-[#C07C88] inline-flex items-center gap-1" data-testid="download-pdf">
                  <FileDown size={12} /> Baixar PDF
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
